import React, { useState, useContext } from 'react';
import { Grid2 } from '@mui/material';
import { NotificationContext } from '@contexts/NotificationContext';
import { getTranslation } from '@helpers/languageTranslations';
import { useAtom } from 'jotai';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { useTranslation } from '@stateManagement/TranslationContext';
import { postMarketingCloudForm } from '@services/marketingCloudPublicService';
import { SubscriptionBoxStyles as classes } from './styleObjects/SubscriptionBoxStyles';
import { redirectToThankyou } from '@helpers/form';
import { getLanguageByCode } from '@helpers/languages';
import { useUTMFormValues } from '@hooks/useUTMFormValues';
import { Input, Checkbox, Text, Button, colors, useRecaptcha } from '@web-for-marketing/react-ui';
import { CMSLink } from '@components/CMSLink';
import { logError } from '@helpers/errors';

/* eslint-disable camelcase */
const initialFormValues = {
    first_name: '',
    last_name: '',
    email: '',
    opt_in: false,
};
/* eslint-enable camelcase */

export function SubscribeBlogForm(): React.ReactElement {
    const notificationContext = useContext(NotificationContext);
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const { recaptchaSiteKey } = useStaticValues();
    const { selectedLanguageTranslation } = useTranslation();
    const [formValues, setFormValues] = useUTMFormValues(initialFormValues);
    const [enableRecaptcha, RecaptchaComponent, token, refresh] = useRecaptcha(recaptchaSiteKey);
    const [disableButton, setDisableButton] = useState(false);
    const subscribeFormTranslation = getTranslation(selectedLanguageTranslation, 'authorBiography', 'subscribeFormMC');
    const salesforceFormLanguage = getTranslation(selectedLanguageTranslation, 'salesforce');
    const language = getLanguageByCode(selectedLanguage);
    const isEUForm = language ? language.isEuLanguage : false;

    async function handleSubmit(event: React.FormEvent): Promise<void> {
        event.preventDefault();
        setDisableButton(true);
        try {
            if (isEUForm) {
                await postMarketingCloudForm(
                    subscribeFormTranslation.formId,
                    {
                        ...formValues,
                        ['Form_Language_Country__c']: salesforceFormLanguage.formLanguage,
                        ['language_selection']: selectedLanguage,
                    },
                    token
                );
            } else {
                await postMarketingCloudForm(subscribeFormTranslation.formId, formValues, token);
            }
            redirectToThankyou('', selectedLanguage, undefined, true);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : error;
            notificationContext.show(errorMessage, 'error');
            refresh();
            setDisableButton(false);
        }
    }

    function toggleOptIn(): void {
        const currentValue = formValues['opt_in'];
        updateFieldValue('opt_in', !currentValue);
    }

    function updateFieldValue(field: string, value: string | boolean): void {
        setFormValues((currentFormValues) => ({ ...currentFormValues, [field]: value }));
    }

    return (
        <Grid2
            container
            component='form'
            onSubmit={(event) => {
                handleSubmit(event).catch((error) => logError(error, 'submitting become a reseller form'));
            }}
            spacing={2}
        >
            <Grid2 size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <Input
                    required
                    onFocus={enableRecaptcha}
                    placeholder={subscribeFormTranslation.firstName}
                    name='first_name'
                    id='first_name'
                    value={formValues['first_name']}
                    onChange={(event) => updateFieldValue('first_name', event.target.value)}
                    label={subscribeFormTranslation.firstName}
                    hiddenLabel
                    labelColor='transparent'
                    backgroundColor={colors.core.snow}
                    css={classes.input}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <Input
                    onFocus={enableRecaptcha}
                    placeholder={subscribeFormTranslation.lastName}
                    name='last_name'
                    id='last_name'
                    value={formValues['last_name']}
                    onChange={(event) => updateFieldValue('last_name', event.target.value)}
                    required
                    label={subscribeFormTranslation.lastName}
                    hiddenLabel
                    labelColor='transparent'
                    backgroundColor={colors.core.snow}
                    css={classes.input}
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <Input
                    onFocus={enableRecaptcha}
                    placeholder={subscribeFormTranslation.email}
                    name='emailAddress'
                    id='emailAddress'
                    value={formValues['email']}
                    onChange={(event) => updateFieldValue('email', event.target.value)}
                    required
                    type='email'
                    label={subscribeFormTranslation.email}
                    hiddenLabel
                    labelColor='transparent'
                    backgroundColor={colors.core.snow}
                    css={classes.input}
                />
            </Grid2>
            <Checkbox
                id='subscribe-checkbox'
                checked={formValues['opt_in']}
                value={formValues['opt_in']}
                onFocus={enableRecaptcha}
                onChange={toggleOptIn}
                uncheckedColor={colors.core.snow}
                checkedColor={colors.core.snow}
                css={classes.consentContainer}
                labelPlacement='top'
                label={
                    <Text variant='body2' css={classes.label}>
                        {subscribeFormTranslation.consentTextSentences}
                        <CMSLink
                            type='link'
                            rel='noopener noreferrer'
                            target='_blank'
                            href={subscribeFormTranslation.consentLink}
                            css={classes.linkStyles}
                        >
                            {subscribeFormTranslation.consentLinkText}
                        </CMSLink>
                    </Text>
                }
            />
            <Grid2 size={{ xs: 12, sm: 'auto', md: 12, lg: 'auto' }}>
                {RecaptchaComponent}
                <Button
                    type='submit'
                    fullWidth
                    variant='tertiary'
                    size='medium'
                    disabled={disableButton}
                    id='blogsubscribebutton'
                >
                    {subscribeFormTranslation.subscribe}
                </Button>
            </Grid2>
        </Grid2>
    );
}
